import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';
import { useError } from './ErrorContext';
import { useApiCaller } from '../utils/ApiUtilities';
import { getLogoutUrl, getMeUrl } from '../utils/UrlUtilities';
import {
    SESSION_IDLE_TIMEOUT_MS,
    SESSION_WARN_BEFORE_MS,
    SESSION_LOGOUT_EVENT_KEY,
} from '../utils/Constants';

// User interactions that count as "activity". These bubble to window, so quiz option
// clicks, typing, and scrolling all reset the idle clock. Activity is deliberately NOT
// tied to API calls — an engaged user (even with zero network calls) is never logged out.
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'click', 'keydown', 'input', 'scroll', 'touchstart'];
const TICK_MS = 1000; // poll cadence — 1s so the warning countdown is smooth

// App-wide inactivity guard: logs the user out after SESSION_IDLE_TIMEOUT_MS of no
// interaction (with a countdown warning first), auto-logs-out across tabs, and shows a
// clear message. Renders nothing unless a warning is active.
export default function SessionManager() {
    const { userState, logoutState } = useUser();
    const { showSuccess } = useError();
    const navigate = useNavigate();
    const CallApi = useApiCaller();
    const isLoggedIn = !!userState?.isLoggedIn;

    const [showWarning, setShowWarning] = useState(false);
    const [secondsLeft, setSecondsLeft] = useState(Math.ceil(SESSION_WARN_BEFORE_MS / 1000));

    const lastActivityRef = useRef(Date.now());
    const showWarningRef = useRef(false);
    const endingRef = useRef(false);

    // Track activity into a ref (no re-render). Any interaction while the warning is up
    // dismisses it and resets the idle clock.
    useEffect(() => {
        const onActivity = () => {
            lastActivityRef.current = Date.now();
            if (showWarningRef.current) {
                showWarningRef.current = false;
                setShowWarning(false);
            }
        };
        ACTIVITY_EVENTS.forEach(ev => window.addEventListener(ev, onActivity, { passive: true }));
        return () => ACTIVITY_EVENTS.forEach(ev => window.removeEventListener(ev, onActivity));
    }, []);

    // Reset the idle clock whenever the user (re)logs in.
    useEffect(() => {
        if (isLoggedIn) {
            lastActivityRef.current = Date.now();
            endingRef.current = false;
            showWarningRef.current = false;
            setShowWarning(false);
        }
    }, [isLoggedIn]);

    const endSession = (reason) => {
        if (endingRef.current) return;
        endingRef.current = true;
        showWarningRef.current = false;
        setShowWarning(false);
        // Best-effort server-side revoke (reads the token before logoutState clears it).
        try { CallApi({ url: getLogoutUrl(), method: 'POST', token: true, silent: true }).catch(() => {}); } catch (_) { /* ignore */ }
        if (reason === 'idle') {
            showSuccess("You've been logged out due to inactivity. Please log in again.");
        }
        logoutState(); // broadcasts to other tabs
        navigate('/login');
    };

    // Idle poll — only while logged in.
    useEffect(() => {
        if (!isLoggedIn) return undefined;
        const id = setInterval(() => {
            const idle = Date.now() - lastActivityRef.current;
            if (idle >= SESSION_IDLE_TIMEOUT_MS) {
                endSession('idle');
            } else if (idle >= SESSION_IDLE_TIMEOUT_MS - SESSION_WARN_BEFORE_MS) {
                setSecondsLeft(Math.max(0, Math.ceil((SESSION_IDLE_TIMEOUT_MS - idle) / 1000)));
                if (!showWarningRef.current) { showWarningRef.current = true; setShowWarning(true); }
            }
        }, TICK_MS);
        return () => clearInterval(id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoggedIn]);

    // Cross-tab logout: another tab ended the session → end it here too (no re-broadcast).
    useEffect(() => {
        const onStorage = (e) => {
            if (e.key === SESSION_LOGOUT_EVENT_KEY && isLoggedIn && !endingRef.current) {
                endingRef.current = true;
                showWarningRef.current = false;
                setShowWarning(false);
                showSuccess('You have been logged out.');
                logoutState({ broadcast: false });
                navigate('/login');
            }
        };
        window.addEventListener('storage', onStorage);
        return () => window.removeEventListener('storage', onStorage);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoggedIn]);

    const staySignedIn = () => {
        lastActivityRef.current = Date.now();
        showWarningRef.current = false;
        setShowWarning(false);
        // Ping an authenticated endpoint so the token is refreshed if it was near expiry.
        try { CallApi({ url: getMeUrl(), method: 'GET', token: true, silent: true }).catch(() => {}); } catch (_) { /* ignore */ }
    };

    if (!isLoggedIn || !showWarning) return null;

    return (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: '1rem' }}>
            <div style={{ backgroundColor: '#fff', borderRadius: '0.9rem', boxShadow: '0 10px 25px -5px rgba(0,0,0,0.3)', width: '90%', maxWidth: '420px', overflow: 'hidden' }}>
                <div style={{ background: 'linear-gradient(145deg,#7c3aed,#5b21b6)', color: '#fff', padding: '1rem 1.25rem' }}>
                    <h3 style={{ margin: 0, fontSize: '1.2rem', fontWeight: 700 }}>Still there?</h3>
                </div>
                <div style={{ padding: '1.25rem' }}>
                    <p style={{ color: '#374151', marginBottom: '1rem' }}>
                        You'll be logged out in <strong>{secondsLeft >= 60 ? `${Math.floor(secondsLeft / 60)}m ${String(secondsLeft % 60).padStart(2, '0')}s` : `${secondsLeft}s`}</strong> due to inactivity. Your test progress is saved.
                    </p>
                    <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                        <button onClick={() => endSession('manual')} className="btn btn-outline-secondary btn-sm">Log out now</button>
                        <button
                            onClick={staySignedIn}
                            className="btn btn-sm text-white"
                            style={{ background: 'linear-gradient(145deg,#7c3aed,#5b21b6)', border: 'none', fontWeight: 600 }}
                        >
                            Stay signed in
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
